// Display preferences (R-5.7): text size 100–200%, theme (system/light/dark), high contrast.
// Persisted per device in localStorage (wrapped in try/catch) and applied to <html> before first render.
import { createStore, useStore } from './store';
import { readJson, writeJson, STORAGE_KEYS } from './storage';

export const TEXT_SCALES = [1, 1.25, 1.5, 1.75, 2] as const;
export type TextScale = (typeof TEXT_SCALES)[number];
export type ThemePreference = 'system' | 'light' | 'dark';

export interface Preferences {
  textScale: TextScale;
  theme: ThemePreference;
  highContrast: boolean;
}

export const DEFAULT_PREFERENCES: Preferences = { textScale: 1, theme: 'system', highContrast: false };

/** Base font size as a percentage of the browser default (16px × 112.5% = 18px, ARCHITECTURE §8). */
const BASE_PERCENT = 112.5;

function sanitise(p: Partial<Preferences> | null | undefined): Preferences {
  const scale = TEXT_SCALES.includes(p?.textScale as TextScale) ? (p?.textScale as TextScale) : 1;
  const theme: ThemePreference = p?.theme === 'light' || p?.theme === 'dark' ? p.theme : 'system';
  return { textScale: scale, theme, highContrast: p?.highContrast === true };
}

export function loadPreferences(): Preferences {
  return sanitise(readJson<Partial<Preferences> | null>(STORAGE_KEYS.preferences, null));
}

export const preferencesStore = createStore<Preferences>(DEFAULT_PREFERENCES);

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

export function applyPreferences(p: Preferences, root: HTMLElement = document.documentElement): void {
  root.style.fontSize = `${BASE_PERCENT * p.textScale}%`;
  const dark = p.theme === 'dark' || (p.theme === 'system' && systemPrefersDark());
  root.dataset.theme = dark ? 'dark' : 'light';
  if (p.highContrast) root.dataset.contrast = 'high';
  else delete root.dataset.contrast;
}

export function setPreferences(patch: Partial<Preferences>): void {
  const next = sanitise({ ...preferencesStore.get(), ...patch });
  preferencesStore.set(next);
  writeJson(STORAGE_KEYS.preferences, next);
  applyPreferences(next);
}

/** Call once at boot (main.tsx). Also follows OS dark-mode changes while theme = system. */
export function initPreferences(): void {
  const p = loadPreferences();
  preferencesStore.set(p);
  applyPreferences(p);
  try {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      applyPreferences(preferencesStore.get());
    });
  } catch {
    // matchMedia unavailable
  }
}

export function usePreferences(): Preferences {
  return useStore(preferencesStore);
}
